import React from "react";

import { siteMeta } from "content/siteContent";

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function upsertLink(rel, href) {
  let element = document.head.querySelector(`link[rel="${rel}"]`);

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }

  element.setAttribute("href", href);
}

function toAbsoluteUrl(path) {
  if (!path) {
    return siteMeta.siteUrl;
  }

  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }

  return `${siteMeta.siteUrl}${path.startsWith("/") ? path : `/${path}`}`;
}

export default function usePageMeta({
  title,
  description,
  path,
  image = siteMeta.defaultImage,
  type = "website",
  robots = "index,follow",
  structuredData,
}) {
  const structuredDataString = structuredData
    ? JSON.stringify(structuredData)
    : null;

  React.useEffect(() => {
    const canonicalUrl = toAbsoluteUrl(path || window.location.pathname);
    const imageUrl = toAbsoluteUrl(image);

    document.title = title || siteMeta.defaultTitle;

    upsertMeta('meta[name="description"]', {
      name: "description",
      content: description || siteMeta.defaultDescription,
    });
    upsertMeta('meta[name="theme-color"]', {
      name: "theme-color",
      content: siteMeta.themeColor,
    });
    upsertMeta('meta[name="robots"]', {
      name: "robots",
      content: robots,
    });

    upsertMeta('meta[property="og:type"]', {
      property: "og:type",
      content: type,
    });
    upsertMeta('meta[property="og:site_name"]', {
      property: "og:site_name",
      content: siteMeta.siteName,
    });
    upsertMeta('meta[property="og:title"]', {
      property: "og:title",
      content: title || siteMeta.defaultTitle,
    });
    upsertMeta('meta[property="og:description"]', {
      property: "og:description",
      content: description || siteMeta.defaultDescription,
    });
    upsertMeta('meta[property="og:url"]', {
      property: "og:url",
      content: canonicalUrl,
    });
    upsertMeta('meta[property="og:image"]', {
      property: "og:image",
      content: imageUrl,
    });

    upsertMeta('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });
    upsertMeta('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: title || siteMeta.defaultTitle,
    });
    upsertMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: description || siteMeta.defaultDescription,
    });
    upsertMeta('meta[name="twitter:image"]', {
      name: "twitter:image",
      content: imageUrl,
    });

    upsertLink("canonical", canonicalUrl);

    const existingStructuredData = document.head.querySelector(
      "#ignitum-structured-data"
    );

    if (existingStructuredData) {
      existingStructuredData.remove();
    }

    if (structuredDataString) {
      const script = document.createElement("script");
      script.id = "ignitum-structured-data";
      script.type = "application/ld+json";
      script.textContent = structuredDataString;
      document.head.appendChild(script);
    }
  }, [description, image, path, robots, structuredDataString, title, type]);
}
